import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Field } from "@/components/common";
import { api, errMsg, fieldErrs } from "@/lib/api";
import { EmojiPicker, FormDialog } from "@/components/kids/kidsUi";
import { KidAvatar } from "@/components/Avatars";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SCHEDULES = [
  { id: "once", label: "One time" },
  { id: "daily", label: "Every day" },
  { id: "weekdays", label: "On chosen weekdays" },
  { id: "weekly", label: "Once a week" },
];

export default function ChoreForm({ hubId, kids, chore, onClose, onSaved }) {
  const [f, setF] = useState(() => chore ? {
    title: chore.title, note: chore.note || "", icon: chore.icon, reward: String(chore.reward), minutes: String(chore.minutes || 0), kid_ids: [chore.kid_id],
    schedule: chore.schedule.type, days: chore.schedule.days, due_date: chore.schedule.due_date || "",
  } : { title: "", note: "", icon: "⭐", reward: "1.00", minutes: "0", kid_ids: [], schedule: "daily", days: [], due_date: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggle = (k, v) => set(k, f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v]);

  const save = async () => {
    const errs = {};
    if (!f.title.trim()) errs.title = "Add a title";
    if (!chore && !f.kid_ids.length) errs.kids = "Pick at least one kid";
    const r = Number(f.reward);
    if (f.reward === "" || Number.isNaN(r) || r < 0 || r > 100) errs.reward = "Reward must be between $0.00 and $100.00";
    const mins = Number(f.minutes || 0);
    if (!Number.isInteger(mins) || mins < 0 || mins > 240) errs.minutes = "Minutes must be between 0 and 240";
    if (f.schedule === "weekdays" && !f.days.length) errs.days = "Pick at least one day";
    if (f.schedule === "weekly" && f.days.length !== 1) errs.days = "Pick one day";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const body = { ...f, reward: r, minutes: Number(f.minutes || 0), due_date: f.due_date || null };
      if (chore) await api.put(`/organizations/${hubId}/chores/${chore.id}`, body);
      else await api.post(`/organizations/${hubId}/chores`, body);
      toast.success(chore ? "Chore updated" : "Chore added");
      onSaved();
    } catch (e) {
      setErrors(fieldErrs(e) || {});
      if (!fieldErrs(e)) toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <FormDialog open onClose={onClose} title={chore ? "Edit chore" : "New chore"} description={chore ? "Changes apply to today and later. Approved chores keep their reward." : "Pick who does it and how often."} testid="chore-form">
      <div className="space-y-4">
        <Field label="Title" htmlFor="chore-title" error={errors.title} errorTestid="chore-title-error">
          <input id="chore-title" className="q-input" value={f.title} maxLength={80} aria-invalid={!!errors.title} placeholder="Read 20 minutes" onChange={(e) => set("title", e.target.value)} data-testid="chore-title-input" />
        </Field>
        <Field label="Icon"><EmojiPicker value={f.icon} onChange={(v) => set("icon", v)} testid="chore-icon" /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Reward ($)" htmlFor="chore-reward" error={errors.reward} errorTestid="chore-reward-error">
            <input id="chore-reward" type="number" step="0.01" min="0" max="100" className="q-input" value={f.reward} aria-invalid={!!errors.reward} onChange={(e) => set("reward", e.target.value)} data-testid="chore-reward-input" />
          </Field>
          <Field label="Screen-time minutes (optional)" htmlFor="chore-minutes" error={errors.minutes} errorTestid="chore-minutes-error">
            <input id="chore-minutes" type="number" step="1" min="0" max="240" className="q-input" value={f.minutes} onChange={(e) => set("minutes", e.target.value)} data-testid="chore-minutes-input" />
          </Field>
          <Field label="Note (optional)" htmlFor="chore-note">
            <input id="chore-note" className="q-input" value={f.note} maxLength={200} onChange={(e) => set("note", e.target.value)} data-testid="chore-note-input" />
          </Field>
        </div>
        {!chore && (
          <Field label="Kids" error={errors.kids} errorTestid="chore-kids-error">
            <div className="flex flex-wrap gap-2">
              {kids.map((k) => (
                <button key={k.id} type="button" onClick={() => toggle("kid_ids", k.id)} aria-pressed={f.kid_ids.includes(k.id)} data-testid={`chore-kid-${k.name.toLowerCase()}`}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-bold text-q-text"
                  style={{ borderColor: f.kid_ids.includes(k.id) ? "rgb(var(--glow-kids))" : "rgb(var(--q-border))", background: f.kid_ids.includes(k.id) ? "rgba(var(--glow-kids),0.14)" : "rgb(var(--q-raised))" }}>
                  <KidAvatar avatar={k.avatar} size={24} /> {k.name}
                </button>
              ))}
            </div>
          </Field>
        )}
        <Field label="How often">
          <div className="grid grid-cols-2 gap-2">
            {SCHEDULES.filter((s) => !chore || (s.id === "once") === (chore.schedule.type === "once")).map((s) => (
              <button key={s.id} type="button" onClick={() => setF((x) => ({ ...x, schedule: s.id, days: s.id === "weekly" ? x.days.slice(0, 1) : x.days }))} data-testid={`chore-schedule-${s.id}`} aria-pressed={f.schedule === s.id}
                className={`q-btn justify-center py-2 text-xs ${f.schedule === s.id ? "q-btn-primary" : "q-btn-secondary"}`}>{s.label}</button>
            ))}
          </div>
        </Field>
        {(f.schedule === "weekdays" || f.schedule === "weekly") && (
          <Field label={f.schedule === "weekly" ? "Which day" : "Which days"} error={errors.days} errorTestid="chore-days-error">
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((d, i) => (
                <button key={d} type="button" aria-pressed={f.days.includes(i)} data-testid={`chore-day-${d.toLowerCase()}`}
                  onClick={() => (f.schedule === "weekly" ? set("days", [i]) : toggle("days", i))}
                  className={`q-btn min-w-[48px] justify-center px-2 py-2 text-xs ${f.days.includes(i) ? "q-btn-primary" : "q-btn-secondary"}`}>{d}</button>
              ))}
            </div>
          </Field>
        )}
        {f.schedule === "once" && (
          <Field label="Due date (optional)" htmlFor="chore-due">
            <input id="chore-due" type="date" className="q-input" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="chore-due-input" />
          </Field>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button className="q-btn q-btn-ghost" onClick={onClose} data-testid="chore-cancel-btn">Cancel</button>
          <button className="q-btn q-btn-primary" onClick={save} disabled={busy} data-testid="chore-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save chore</button>
        </div>
      </div>
    </FormDialog>
  );
}
